// frontend/src/pages/ProductDetail.jsx - COMPLETELY CLEANED
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getProductBySlug } from "../api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import Loader from "../components/UI/Loader";
import ProductReactions from "../components/Product/ProductReactions";
import ProductComments from "../components/Product/ProductComments";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  faArrowLeft,
  faExclamationTriangle,
  faTractor,
  faShoppingCart,
  faBox,
  faTag,
  faCheckCircle,
  faSpinner,
  faMoneyBillWave,
  faClock,
  faArrowRight,
  faPhone,
  faGlobe,
  faCalendarCheck,
  faRuler,
  faWeightScale,
  faInfoCircle,
  faMinus,
  faPlus,
  faShare,
  faShip,
  faClipboardCheck,
  faPercent,
  faGift,
  faRocket,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import SEO from "../components/SEO/SEO";

// ============================================
// CONSTANTS
// ============================================
const PRE_ORDER_SIZES = [25, 50];
const USD_TO_SLL_RATE = 24.65;

const GROWPRO_PRICING = {
  25: {
    regular: 105.0,
    preOrder: 98.0,
    deposit: 49.0,
    balance: 49.0,
    savingsPercent: 7,
  },
  50: {
    regular: 195.0,
    preOrder: 180.0,
    deposit: 90.0,
    balance: 90.0,
    savingsPercent: 8,
  },
};

const BULK_DISCOUNTS = [
  { quantity: 25, label: "25kg (Starter)", discountPercent: 5 },
  { quantity: 50, label: "50kg (Farmer)", discountPercent: 10 },
  { quantity: 100, label: "100kg (Wholesale)", discountPercent: 15 },
  { quantity: 500, label: "500kg (Bulk Farmer)", discountPercent: 20 },
  { quantity: 1000, label: "1 Ton (Enterprise)", discountPercent: 25 },
];

const TRUST_BADGES = [
  "NRA Registered",
  "Quality Assured",
  "Trusted Partner",
  "Premium Quality",
  "Globally Sourced",
];

// ============================================
// MAIN COMPONENT
// ============================================
const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem, getItemCount } = useCart();
  const { currency } = useCurrency();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(25);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [message, setMessage] = useState(null);

  const userType = user?.userType || "customer";
  const isFarmer = userType === "farmer";

  const availableSizes = product?.availableSizes || [2, 10, 25, 50];
  const images = product?.images || [];
  const specifications = product?.specifications || {};
  const benefits = product?.benefits || [];
  const stockStatus = product?.stockStatus || "out_of_stock";
  const isInStock = stockStatus !== "out_of_stock" && product?.stock > 0;
  const itemInCart = product?._id ? getItemCount(product._id) : 0;

  // ============================================
  // FETCH PRODUCT
  // ============================================
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await getProductBySlug(slug);
        setProduct(data);
        setQuantity(25);
      } catch (err) {
        console.error("Error fetching product:", err);
        setError(err.response?.data?.message || "Product not found");
        setTimeout(() => navigate("/products"), 3000);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug, navigate]);

  // ============================================
  // PRICE CALCULATION
  // ============================================
  const priceData = useMemo(() => {
    if (!product) {
      return {
        perUnit: 0,
        total: 0,
        discountPercent: 0,
        isPreOrder: false,
        deposit: 0,
        balance: 0,
        savings: 0,
        regularTotal: 0,
      };
    }

    const isPreOrderSize = PRE_ORDER_SIZES.includes(quantity);
    let perUnit = product.basePrice?.usd || 4.2;
    let discountPercent = 0;
    let regularTotal = 0;
    let deposit = 0;
    let balance = 0;
    let savings = 0;

    if (isPreOrderSize) {
      const pricing = GROWPRO_PRICING[quantity];
      if (pricing) {
        perUnit = pricing.preOrder / quantity;
        regularTotal = pricing.regular;
        deposit = pricing.deposit;
        balance = pricing.balance;
        savings = pricing.regular - pricing.preOrder;
        discountPercent = pricing.savingsPercent;
      }
    } else {
      const applicableBulk = [...BULK_DISCOUNTS]
        .sort((a, b) => b.quantity - a.quantity)
        .find((tier) => quantity >= tier.quantity);
      if (applicableBulk) {
        discountPercent = applicableBulk.discountPercent;
        perUnit =
          product.basePrice?.usd * (1 - discountPercent / 100) ||
          4.2 * (1 - discountPercent / 100);
      }
      regularTotal = perUnit * quantity;
    }

    const total = perUnit * quantity;

    return {
      perUnit: parseFloat(perUnit.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
      discountPercent: parseFloat(discountPercent.toFixed(1)),
      isPreOrder: isPreOrderSize,
      deposit: deposit,
      balance: balance,
      savings: savings,
      regularTotal: regularTotal,
    };
  }, [product, quantity]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleQuantityChange = useCallback((newQuantity) => {
    const minQty = 1;
    let finalQuantity = Math.max(minQty, newQuantity);
    setQuantity(Math.max(minQty, finalQuantity));
  }, []);

  const handleAddToCart = async () => {
    if (!product) {
      setMessage({ type: "error", text: "Product not found" });
      return;
    }

    if (!user) {
      setMessage({
        type: "warning",
        text: "Please login to add items to your cart",
      });
      setTimeout(() => {
        setMessage(null);
        navigate("/login");
      }, 2000);
      return;
    }

    setAdding(true);
    setMessage(null);

    try {
      await addItem(product._id, quantity);
      setMessage({
        type: "success",
        text: `${product.name} added! (${quantity} kg)${priceData.isPreOrder ? " - 50% deposit required" : ""}`,
      });
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error adding to cart",
      });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setAdding(false);
    }
  };

  const handleWhatsAppOrder = () => {
    if (!product) return;
    const message = `Hi Kenali Enterprise, I'm interested in your product: ${product.name}.
    
📦 Product: ${product.name}
📊 Category: ${product.category?.replace(/-/g, " ") || "Product"}
💰 Price: $${priceData.perUnit.toFixed(2)}/kg
📦 Quantity: ${quantity} kg
📋 Subtotal: $${priceData.total.toFixed(2)}
${priceData.isPreOrder ? `📋 Pre-Order: $${priceData.deposit.toFixed(2)} deposit, $${priceData.balance.toFixed(2)} balance` : ""}

Can you help me with the order process?`;
    window.open(
      `https://wa.me/23231030808?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  const handleShare = async () => {
    if (!product) return;
    const shareData = {
      title: `${product.name} - Kenali Enterprise`,
      text: `Check out ${product.name} at Kenali Enterprise! 🌾\n\nPrice: $${priceData.perUnit.toFixed(2)}/kg\n\nShop now at Kenali Enterprise - Premium Livestock Nutrition Solutions.`,
      url: window.location.href,
    };

    try {
      if (navigator.share && window.innerWidth <= 768) {
        await navigator.share(shareData);
        setMessage({ type: "success", text: "Shared successfully!" });
        setTimeout(() => setMessage(null), 3000);
      } else {
        await navigator.clipboard.writeText(
          `${shareData.text}\n\n${shareData.url}`,
        );
        setMessage({ type: "success", text: "Link copied to clipboard!" });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(
            `${shareData.text}\n\n${shareData.url}`,
          );
          setMessage({ type: "success", text: "Link copied to clipboard!" });
        } catch {
          setMessage({
            type: "error",
            text: "Unable to share. Please try again.",
          });
        }
        setTimeout(() => setMessage(null), 4000);
      }
    }
  };

  const handleCall = () => (window.location.href = "tel:+23231030808");

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;
  if (error || !product) {
    return (
      <div className="container product-detail-error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h2>{error || "Product not found"}</h2>
        <p>Redirecting to products page...</p>
      </div>
    );
  }

  const stockText = isInStock
    ? product.stock < 10
      ? `Only ${product.stock} units left`
      : `${product.stock} units in stock`
    : "Out of Stock - Available for Pre-Order";
  const stockStatusClass = isInStock ? "in-stock" : "pre-order";

  return (
    <>
      <SEO
        title={`${product.name} - Premium Livestock Feed`}
        description={`${product.name} - ${product.shortDescription || product.description?.substring(0, 150) || "Premium livestock feed supplement"}`}
        keywords={`${product.name}, livestock feed, animal nutrition, ${product.category}, Kenali Enterprise`}
        canonical={`https://kenalienterprise.com/products/${product.slug}`}
        image={product.images?.[0] || "https://kenalienterprise.com/logo.png"}
      />
      <motion.div
        className="container product-detail-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <button className="back-button" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>

        {message && (
          <div className={`message ${message.type}`}>
            <FontAwesomeIcon
              icon={message.type === "success" ? faCheck : faTimes}
            />
            <span>{message.text}</span>
          </div>
        )}

        <div className="preorder-banner">
          <FontAwesomeIcon icon={faCalendarCheck} />
          <div>
            <strong>Pre-Order Available!</strong>
            <span>Reserve your Kenali GrowPro™ with a 50% deposit.</span>
          </div>
          <Link to="/preorder" className="preorder-banner-btn">
            Pre-Order Now <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>

        <div className="product-detail-grid">
          <div className="product-gallery">
            <div className="main-image">
              {images.length > 0 && images[activeImage] ? (
                <img src={images[activeImage]} alt={product.name} />
              ) : (
                <div className="image-placeholder">
                  <FontAwesomeIcon icon={faBox} />
                  <span>No Image Available</span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="thumbnail-grid">
                {images.map((img, idx) => (
                  <motion.div
                    key={idx}
                    className={`thumbnail-item ${activeImage === idx ? "active" : ""}`}
                    onClick={() => setActiveImage(idx)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div className="product-info">
            <h1>{product.name}</h1>
            <div className="product-tags">
              <span className="tag">
                <FontAwesomeIcon icon={faTag} />{" "}
                {product.category?.replace(/-/g, " ") || "Product"}
              </span>
              <span className="tag origin">
                <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
              </span>
              <span className="tag preorder">
                <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order Available
              </span>
            </div>

            <div className="trust-badges">
              {TRUST_BADGES.map((label) => (
                <span key={label} className="trust-badge">
                  <FontAwesomeIcon icon={faCheckCircle} /> {label}
                </span>
              ))}
              <span className="trust-badge preorder">
                <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
              </span>
            </div>

            <div className="size-section">
              <h4>
                <FontAwesomeIcon icon={faRuler} /> Available Sizes
              </h4>
              <div className="size-tags">
                {availableSizes.map((size) => {
                  const isPreOrderSize = PRE_ORDER_SIZES.includes(size);
                  return (
                    <span
                      key={size}
                      className={`size-tag ${isPreOrderSize ? "preorder" : ""}`}
                    >
                      {size}kg
                      {isPreOrderSize && (
                        <span className="preorder-label">Pre-Order Only</span>
                      )}
                    </span>
                  );
                })}
              </div>
              <p className="preorder-note">
                <FontAwesomeIcon icon={faInfoCircle} />
                25kg and 50kg sizes available for pre-order with 50% deposit.
              </p>
            </div>

            <div className="price-section">
              {priceData.discountPercent > 0 && (
                <span className="discount-badge">
                  <FontAwesomeIcon icon={faPercent} /> Save{" "}
                  {Math.round(priceData.discountPercent)}%
                </span>
              )}
              <span className="current-price">
                <CurrencyDisplay amountUSD={priceData.perUnit} />
              </span>
              <span className="unit">/kg</span>
              {priceData.isPreOrder && priceData.regularTotal > 0 && (
                <span className="original-price">
                  <CurrencyDisplay
                    amountUSD={priceData.regularTotal / quantity}
                  />
                </span>
              )}
              <span className="preorder-badge-label">Pre-Order Price</span>
            </div>

            {priceData.isPreOrder && (
              <div className="deposit-breakdown">
                <span className="deposit-item">
                  <strong>Deposit (50%):</strong>{" "}
                  <CurrencyDisplay amountUSD={priceData.deposit} />
                </span>
                <span className="deposit-item">
                  <strong>Balance:</strong>{" "}
                  <CurrencyDisplay amountUSD={priceData.balance} />
                </span>
                <span className="deposit-item savings">
                  <strong>You Save:</strong>{" "}
                  <CurrencyDisplay amountUSD={priceData.savings} />
                </span>
              </div>
            )}

            {isFarmer && (
              <div className="farmer-notice">
                <FontAwesomeIcon icon={faTractor} />
                <div>
                  <strong>Farmer Bulk Pricing Active</strong>
                  <p>Minimum order: 25kg</p>
                </div>
              </div>
            )}

            <div className="payment-options">
              <strong>
                <FontAwesomeIcon icon={faMoneyBillWave} /> Payment:
              </strong>
              <span>
                Cash on Delivery • Orange Money • Afrimoney • WAVE • Bank
                Transfer
              </span>
            </div>

            <div className="bulk-discounts">
              <h4>
                <FontAwesomeIcon icon={faBox} /> Bulk Discounts
              </h4>
              <ul>
                {BULK_DISCOUNTS.map((option, idx) => (
                  <li
                    key={idx}
                    className={quantity >= option.quantity ? "active" : ""}
                  >
                    <span>
                      <FontAwesomeIcon icon={faGift} /> {option.label}
                    </span>
                    <span>
                      Save {option.discountPercent}% →{" "}
                      <CurrencyDisplay
                        amountUSD={4.2 * (1 - option.discountPercent / 100)}
                        compact
                      />{" "}
                      /kg
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="product-description">
              {product.shortDescription && <h4>Overview</h4>}
              <p>{product.description}</p>
            </div>

            {benefits.length > 0 && (
              <div className="product-benefits">
                <h4>
                  <FontAwesomeIcon icon={faRocket} /> Key Benefits
                </h4>
                <ul>
                  {benefits.map((benefit, idx) => (
                    <li key={idx}>
                      <FontAwesomeIcon icon={faCheckCircle} /> {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.usageInstructions && (
              <div className="usage-instructions">
                <h4>
                  <FontAwesomeIcon icon={faInfoCircle} /> Usage Instructions
                </h4>
                <p>{product.usageInstructions}</p>
              </div>
            )}

            <div className="product-meta">
              <p>
                <strong>
                  <FontAwesomeIcon icon={faGlobe} /> Origin:
                </strong>
                <span>Globally Sourced</span>
              </p>
              <p>
                <strong>
                  <FontAwesomeIcon icon={faShip} /> Port Discharge:
                </strong>
                <span>Freetown, Sierra Leone</span>
              </p>
              <p>
                <strong>
                  <FontAwesomeIcon icon={faClock} /> Availability:
                </strong>
                <span className={`stock-status ${stockStatusClass}`}>
                  {stockText}
                </span>
              </p>
              <p className="preorder-meta">
                <strong>
                  <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order:
                </strong>
                <span>Reserve now with 50% deposit. Stock arriving soon.</span>
              </p>
              {product.packaging && (
                <p>
                  <strong>
                    <FontAwesomeIcon icon={faBox} /> Packaging:
                  </strong>
                  <span>{product.packaging}</span>
                </p>
              )}
            </div>

            {Object.keys(specifications).length > 0 && (
              <div className="specifications">
                <h4>
                  <FontAwesomeIcon icon={faClipboardCheck} /> Specifications
                </h4>
                <ul>
                  {Object.entries(specifications).map(([key, value]) => (
                    <li key={key}>
                      <strong>{key}:</strong>
                      <span>{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <ProductReactions productId={product._id} />

            <div className="quantity-selector">
              <div className="quantity-control">
                <label className="quantity-label">
                  <FontAwesomeIcon icon={faWeightScale} /> Quantity (kg)
                </label>
                <div className="quantity-input-wrapper">
                  <button
                    className="qty-btn"
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1}
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    step={1}
                    value={quantity}
                    onChange={(e) =>
                      handleQuantityChange(parseInt(e.target.value) || 1)
                    }
                  />
                  <button
                    className="qty-btn"
                    onClick={() => handleQuantityChange(quantity + 1)}
                    disabled={quantity >= 999}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
                {PRE_ORDER_SIZES.includes(quantity) && (
                  <span className="step-hint preorder-hint">
                    <FontAwesomeIcon icon={faCalendarCheck} /> Pre-order
                    available
                  </span>
                )}
              </div>

              <div className="total-price">
                <div className="total-label">Pre-Order Total</div>
                <div className="total-amount">
                  <CurrencyDisplay amountUSD={priceData.total} />
                </div>
                {priceData.savings > 0 && (
                  <div className="savings">
                    Save{" "}
                    <CurrencyDisplay amountUSD={priceData.savings} compact />
                  </div>
                )}
                {priceData.isPreOrder && (
                  <div className="deposit-info">
                    <FontAwesomeIcon icon={faMoneyBillWave} /> 50% deposit
                    required
                  </div>
                )}
                {itemInCart > 0 && (
                  <div className="in-cart-info">
                    <FontAwesomeIcon icon={faShoppingCart} /> {itemInCart} in
                    cart
                  </div>
                )}
              </div>

              <button
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={adding}
              >
                {adding ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Adding...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCalendarCheck} />{" "}
                    {priceData.isPreOrder ? "Pre-Order Now" : "Add to Cart"}
                  </>
                )}
              </button>

              <div className="preorder-terms">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  Pre-order with 50% deposit. Balance due before
                  pickup/delivery.
                </span>
              </div>
            </div>

            <div className="contact-buttons">
              <button className="whatsapp-btn" onClick={handleWhatsAppOrder}>
                <FontAwesomeIcon icon={faWhatsapp} /> Pre-Order via WhatsApp
              </button>
              <button className="call-btn" onClick={handleCall}>
                <FontAwesomeIcon icon={faPhone} /> Call Us
              </button>
              <button className="share-btn" onClick={handleShare}>
                <FontAwesomeIcon icon={faShare} /> Share
              </button>
            </div>

            <div className="preorder-bottom-cta">
              <Link to="/preorder" className="preorder-bottom-btn">
                <FontAwesomeIcon icon={faCalendarCheck} /> Go to Pre-Order Page
                <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
              </Link>
            </div>
          </div>
        </div>

        <ProductComments productId={product._id} />
      </motion.div>
    </>
  );
};

export default ProductDetail;
